import React from 'react';
import Section from '../components/Section';
import DefaultLayout from '../layouts/DefaultLayout';
import { Body, DesktopOnly, H3, H4, LenLink, MobileOnly, Reference, Ul } from '../util/typography';
import Subsection from '../components/Subsection';
import TextIcon1Column from '../components/TextIcon1Column';
import personIcon from '../assets/content-icons/icon-person-green.png';
import personIconMulberry from '../assets/content-icons/icon-person-mulberry.png';
import ivIconGreen from '../assets/content-icons/icon-iv-green.png';
import pillIconMulberry from '../assets/content-icons/icon-pill-mulberry.png';
import clipboardIconGreen from '../assets/content-icons/icon-clipboard-green.png'
import clipboardIconMulberry from '../assets/content-icons/icon-clipboard-mulberry.png'
import chartDesktopImg from '../assets/content-icons/lenvima-chart-desktop.png';
import chartMobileImg from '../assets/content-icons/lenvima-chart-mobile.png';
import Accordion from '../components/Accordion';
import styled from 'styled-components';
import Chart from '../components/Chart';
import * as colors from '../util/colors';
import { respondTo } from '../util/breakpoints';
import Table from '../components/Table';
import RelatedContent from '../components/RelatedContent';
import { jobCodes } from '../util/data/jobCodes';
import vector from "../assets/utility/icons/vector.svg";
import Button from '../components/Button';

// SEO INFORMATION
const pageMeta = {
    title: `Warnings & Precautions | KEYTRUDA® (pembrolizumab) and LENVIMA® (lenvatinib)`,
    keywords: `keytruda and lenvima safety`,
    description: `Health care professionals can review a summary of warnings and precautions for KEYTRUDA® (pembrolizumab) and LENVIMA® (lenvatinib) including information on immune-mediated adverse reactions.`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudalenvimahcp.com/warnings-and-precautions/","@type":"MedicalAudience","audienceType":"Health Care Professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`,

        `{"@context":"https://schema.org/","url":"https://www.keytrudalenvimahcp.com/warnings-and-precautions/","@type":"MedicalWebPage","name":"Selected Safety Information for KEYTRUDA® (pembrolizumab)","description":"KEYTRUDA® (pembrolizumab) is a monoclonal antibody that belongs to a class of drugs that bind to either the programmed death receptor-1 (PD-1) or the programmed death ligand 1 (PD-L1), blocking the PD-1/PD-L1 pathway, thereby removing inhibition of the immune response, potentially breaking peripheral tolerance and inducing immune-mediated adverse reactions. Immune-mediated adverse reactions, which may be severe or fatal, can occur in any organ system or tissue, can affect more than one body system simultaneously, and can occur at any time after starting treatment or after discontinuation of treatment. Important immune-mediated adverse reactions listed here may not include all possible severe and fatal immune-mediated adverse reactions."}`,

        `{"@context":"https://schema.org/","url":"https://www.keytrudalenvimahcp.com/warnings-and-precautions/","@type":"MedicalWebPage","name":"Selected Safety Information for LENVIMA® (lenvatinib)","description":"Adverse reactions, some of which can be serious or fatal, may occur with LENVIMA® (lenvatinib), including hypertension, cardiac dysfunction, arterial thromboembolic events, hepatotoxicity, renal failure or impairment, proteinuria, diarrhea, fistula formation and gastrointestinal perforation, QT interval prolongation, hypocalcemia, reversible posterior leukoencephalopathy syndrome, hemorrhagic events, impairment of thyroid stimulating hormone suppression/thyroid dysfunction, impaired wound healing, osteonecrosis of the jaw, and embryo-fetal toxicity."}`
    ]
}

const jobCode = jobCodes[0].jobCode;

const LenvimaSubsectionIntro = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const KeytrudaSection = styled(Section)`
    padding-bottom: 30px;

    p{
     line-height: 20px;
    }
    
    ${respondTo.md`
        padding-bottom: 40px;
    `}
`;

const LenvimaSection = styled.div`
    p{
        line-height: 20px;
    }
`;
const KeytrudaAccordion = styled(Subsection)`
    gap: 20px;
    ${respondTo.md`
        padding-top: 40px;
    `}
`;

const AccordionTitle = styled(H4)`
    margin-bottom: 10px;
`;

const FootnotesSection = styled(Section)`
    padding-bottom: 30px;
    ${respondTo.md`
        padding-bottom: 40px;
    `}
`;

const FootnotesSubsection = styled(Subsection)`
    word-wrap: break-word;
    ${respondTo.md`
        padding-top: 40px;
    `}
`;

const CtcaeReference = styled(Reference)`
    div {
        display: flex;
        p {
            display: block;
        }
    }
`;

const IconNoBullets = styled(TextIcon1Column)`
    align-items: flex-start;


    ${respondTo.md`
        align-items: center;
    `}

`;

// const LenLink = styled.div`
// color: ${colors.focus}
// `

const RCLinks = [
    {
        // eyebrow: 'Dosing',
        title: 'Advanced Renal Cell Carcinoma',
        to: '/advanced-renal-cell-carcinoma/#adverse-reaction'
    },
    {
        // eyebrow: 'Resources',
        title: 'Advanced Endometrial Carcinoma',
        to: '/endometrial-carcinoma/#adverse-reaction'
    },
];

const AnchorNavLinks = [
    {
        title: 'On this page: ',
    },
    {
        title: 'KEYTRUDA',
        href: '#keytruda',
        style: 'keytruda',
        icon: vector,
    },
    {
        title: 'LENVIMA',
        href: '#lenvima',
        style: 'lenvima',
        icon: vector,
    }
];

const CustomButton = styled(Button)`
  &.tertiary-dark {
      
      justify-content: space-between;
      align-items: center;
      width: 100% !important;
      color:${colors.blueDark} !important;
      
       &:hover{
                  color:${colors.focus} !important;
                   img {
                       filter: brightness(0%) saturate(100%) 
           invert(15%) sepia(85%) saturate(850%) 
           hue-rotate(190deg) brightness(96%) contrast(90%);
                   }
               }
        &:focus{
        border-radius: unset;
    background: transparent;
    box-shadow: none;
        }
      ${respondTo.md`
          width: 100%;
          justify-content: flex-start;
      `}
      img{
        filter: invert(26%) sepia(99%) saturate(747%) hue-rotate(176deg) brightness(96%) contrast(101%);
        transform: rotate(90deg);

      }
  }
`;


const Safety = ({location}) => {
    return (
        <DefaultLayout 
  location={location} 
  name='Summary of Warnings & Precautions' 
  anchorNavLinks={AnchorNavLinks} 
  pageMeta={pageMeta} 
  jobCode={jobCode}
>
            <KeytrudaSection title='KEYTRUDA' id='keytruda' type='keytruda'>
                <TextIcon1Column>
                    <p>KEYTRUDA® (pembrolizumab) is a monoclonal antibody that belongs to a class of drugs that bind to either the programmed death receptor-1 (PD-1) or the programmed death ligand 1 (PD-L1), blocking the PD-1/PD-L1 pathway, thereby removing inhibition of the immune response, potentially breaking peripheral tolerance and inducing immune-mediated adverse reactions. Immune-mediated adverse reactions, which may be severe or fatal, can occur in any organ system or tissue, can affect more than one body system simultaneously, and can occur at any time after starting treatment or after discontinuation of treatment. Important immune-mediated adverse reactions listed here may not include all possible severe and fatal immune-mediated adverse reactions.</p>
                
                    <p style={{marginTop: '16px'}}>Monitor patients closely for symptoms and signs that may be clinical manifestations of underlying immune-mediated adverse reactions. Early identification and management are essential to ensure safe use of anti–PD&#8209;1/PD&#8209;L1 treatments. Evaluate liver enzymes, creatinine, and thyroid function at baseline and periodically during treatment. In cases of suspected immune-mediated adverse reactions, initiate appropriate workup to exclude alternative etiologies, including infection. Institute medical management promptly, including specialty consultation as appropriate.</p>
                    
                    <p style={{marginTop: '16px'}}>Withhold or permanently discontinue KEYTRUDA depending on severity of the immune-mediated adverse reaction. In general, if KEYTRUDA requires interruption or discontinuation, administer systemic corticosteroid therapy (1 to 2 mg/kg/day prednisone or equivalent) until improvement to Grade 1 or less. Upon improvement to Grade 1 or less, initiate corticosteroid taper and continue to taper over at least 1 month. Consider administration of other systemic immunosuppressants in patients whose immune-mediated adverse reactions are not controlled with corticosteroid therapy.</p>
                    </TextIcon1Column>
                    <Subsection>
                    <CustomButton type='tertiary-dark' to='#ssi-keytruda'>See additional Selected Safety Information for KEYTRUDA</CustomButton>
                    </Subsection>

                
            </KeytrudaSection>
            <Section title='LENVIMA' type='lenvima' id='lenvima' bgColor='cloud'>
                <LenvimaSection>
                    <TextIcon1Column>
                    
                        <p>Adverse reactions, some of which can be serious or fatal, may occur with LENVIMA® (lenvatinib), including hypertension, cardiac dysfunction, arterial thromboembolic events, hepatotoxicity, renal failure or impairment, proteinuria, diarrhea, fistula formation and gastrointestinal perforation, QT interval prolongation, hypocalcemia, reversible posterior leukoencephalopathy syndrome, hemorrhagic events, impairment of thyroid stimulating hormone suppression/thyroid dysfunction, impaired wound healing, osteonecrosis of the jaw, and embryo-fetal toxicity.</p>
                        <p style={{marginTop: '16px'}}>Based on its mechanism of action and data from animal reproduction studies, LENVIMA can cause fetal harm when administered to a pregnant woman. Females of reproductive potential should be advised to use effective contraception during treatment with LENVIMA and for 30 days after the last dose. Based on the severity of the adverse reaction, LENVIMA should be interrupted, reduced, and/or discontinued.</p>
                        
                    </TextIcon1Column>
                </LenvimaSection>
                <Subsection>
                <CustomButton type='tertiary-dark' to='#ssi-lenvima'>See additional Selected Safety Information for LENVIMA</CustomButton>
                {/* <LenvimaSubsectionIntro></LenvimaSubsectionIntro> */}

                </Subsection>
                
            </Section>
            
            <RelatedContent title='Learn more about study-specific selected adverse reactions' links={RCLinks}/>
            <FootnotesSection noIntro>
                
            </FootnotesSection>
        </DefaultLayout>
    )
};

export default Safety;
